import { Injectable } from '@angular/core';
import { CacheService } from '@congacommerce/core';
import { Cart, CartService } from '@congacommerce/ecommerce';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, share, take, tap } from 'rxjs/operators';
import { OrderEntryProvider } from '../../../../modules/common/order-entry/providers/order-entry-provider';
import { ExecutionConfig } from '../../../../modules/common/partnership/models/conventions';
import {
    D365RetrieveUpsertCase,
    UpsertCaseReq
} from '../../../../modules/switch-in/technical-changes/models/d365-upsert-case';
import {
    setV2ConsequentialCartInfo,
    setV2IdPhoneCall,
    setV2OldCartId
} from '../../../../store/actions/order-entry-v2.actions';
import { setCombinedSale, setCommodityCartId, setQuoteId } from '../../../../store/actions/order-entry.actions';
import { MacroFlowType } from '../../../../store/models/flow-type';
import { EglState } from '../../../../store/reducers';
import {
    v2SelectAllProducts,
    v2SelectConsequentialCartType,
    v2SelectIdCase
} from '../../../../store/selectors/order-entry-v2.selectors';
import { selectFlowType, selectSalesProcess } from '../../../../store/selectors/order-entry.selectors';
import { selectAgentInfo, selectCartSegment, selectLead } from '../../../../store/selectors/user.selectors';
import { AptPaymentInstrument } from '../../../enums/apttus/apt-payment-instrument';
import { AptSalesProcess } from '../../../enums/apttus/apt-sales-process';
import { convertSegmentD365toApt, salesProcessOrOperationTypeToFlowType } from '../../../functions/remap.functions';
import { flowTypeUtil, isConsequentialCartActive } from '../../../functions/verifications.functions';
import { EglCartExtended } from '../../../models/apttus/tables/cart/egl-cart-extended';
import { CartToQuoteResponse, SaveQuoteProvider } from '../../../providers/save-quote.provider';
import { TelemetryMetricService } from '../../app/telemetry-metric.service';
import { D365Service } from '../../d365/d365.service';
import { FeatureToggleService } from '../../shared/feature-toggle.service';
import { LoadingService } from '../../shared/loading.service';
import { LocalStorageGenericService } from '../../shared/local-storage-generic.service';
import { LoggerService } from '../../shared/logger.service';
import { PrivateConfigurationService } from '../../shared/private-configuration.service';
import { ApttusService } from '../apttus.service';
import { EglSalesupStateService } from '../tables/egl-salesup-state.service';
import { CartToQuoteRequestService } from './cart-to-quote-request.service';

@Injectable({
    providedIn: 'root'
})
export class CartToQuoteService {
    private pendingQuotes: { [key: string]: Observable<CartToQuoteResponse> } = {};
    constructor(
        private store: Store<EglState>,
        private quotePrv: SaveQuoteProvider,
        private apttusSrv: ApttusService,
        private orderEntryPrv: OrderEntryProvider,
        private logger: LoggerService,
        private telemetrySrv: TelemetryMetricService,
        private cacheSrv: CacheService,
        private salesupStateSrv: EglSalesupStateService,
        private cartToQuoteRequest: CartToQuoteRequestService,
        private localStorageSrv: LocalStorageGenericService,
        private d365Srv: D365Service,
        private toggleSrv: FeatureToggleService,
        private configSrv: PrivateConfigurationService,
        private cartSrv: CartService
    ) {}

    public saveQuoteV2(): Observable<CartToQuoteResponse> {
        /**
         * ☠ ☠ ☠ ☠ GUIDELINE ☠ ☠ ☠ ☠
         *
         * Tutte le logiche di manipolazione carrello/entità varie di salesforce (es. aggiunta, modifica, rimozione prodotti tecnici)
         * devono essere fatte da BE (lato apim / salesforce).
         * Gli unici step triggerati da FE devono essere creazione quote (quote/fromCart),
         * finalizzazione quote (updateQuote), generazione plico (documentOT).
         *
         * Prima di implementare soluzioni fantasiose confrontarsi con F. Lombardi / M. Ricupero
         */
        const cartId = this.currentCartId;
        let caseID: string;
        this.pendingQuotes[cartId] =
            this.pendingQuotes[cartId] ||
            this.salesupStateSrv
                .saveSupState('save-quote-v2')
                // Due pipe perché rxjs protesta per troppe operazioni in una sola pipe
                .pipe(
                    take(1),
                    mergeMap(() => this.manageGetPhoneCall()),
                    // Costruisco la request per la fromCart
                    mergeMap(() => this.cartToQuoteRequest.getCartToQuoteReq()),
                    mergeMap((req) => {
                        //Dalla request mi salvo il caseID necessario per la chiusura del case, in questo caso solo per domiciliazione
                        if (
                            flowTypeUtil(
                                salesProcessOrOperationTypeToFlowType(req?.salesProcess as AptSalesProcess)
                            ).inMacroFlowTypes(MacroFlowType.Domiciliazione)
                        ) {
                            caseID = req?.billingAccount?.paymentTool?.caseID;
                        }
                        // Chiamo la fromCart
                        return (
                            this.quotePrv
                                .createQuoteFromCart(req)
                                // Chiamata a Nexi per aggiunta nuova carta di credito
                                .pipe(mergeMap((quote) => this.wrapperAddNewCreditCard(quote, req)))
                        );
                    }),
                    //chiamo il metodo che mi permette tramite D365 di chiudere il case aperto
                    mergeMap((quote) => (caseID ? this.closeD365Case$(quote, caseID) : of(quote))),
                    // Eseguo gli step successivi
                    mergeMap((quote) => this.apttusSrv.finalizeFlow$(quote)),
                    tap(({ Id: id }) => this.store.dispatch(setQuoteId({ id }))),
                    mergeMap((quote) => this.invalidateCoupon().pipe(map(() => quote)))
                )
                .pipe(
                    mergeMap((quote) => this.manageCombinedSales().pipe(map(() => quote))),
                    mergeMap((quote) => this.manageConsequentialCart().pipe(map(() => quote))),
                    tap(() => this.store.dispatch(setV2OldCartId({ id: this.currentCartId }))),
                    tap((quote) => {
                        this.logger.info(`Cart ${cartId} has generated quote ${quote?.Id}`, quote);
                        this.cacheSrv.refresh(Cart);
                        CartService.deleteLocalCart();
                        this.localStorageSrv.productsZip = null;
                        this.localStorageSrv.commercialFieldsCheck = null;
                    }),
                    // Creazione nuovo carrello. Non viene resettato lo state in questo momento.
                    // Verrà fatto nel destroy della thxPage.
                    mergeMap((quote) =>
                        this.apttusSrv
                            .createNewCart(new EglCartExtended(), null, null, false, false, false)
                            .pipe(map(() => quote))
                    ),
                    catchError((err) => {
                        this.logger.error(null, 'SaveQuote_V2 have an error', err, true, cartId);
                        delete this.pendingQuotes[cartId];
                        throw err;
                    }),
                    LoadingService.loaderOperator('Creazione quote in corso'),
                    this.telemetrySrv.rxTelemetry('save-quote-v2'),
                    share()
                );

        return this.pendingQuotes[cartId];
    }

    private manageCombinedSales(): Observable<boolean> {
        return this.store.select(v2SelectAllProducts).pipe(
            take(1),
            map((products) => products.find((product) => product?.isCombinedSale)),
            tap((productCombined) => {
                // In caso di vendita abbinata commodity + assicurazione
                if (productCombined) {
                    this.logger.info(`'${productCombined?.name}' was configurated like 'Combined sale'`);
                    this.store.dispatch(setCommodityCartId({ commodityCartId: this.currentCartId }));
                    this.store.dispatch(setCombinedSale({ combinedSale: true }));
                }
            }),
            map((productCombined) => !!productCombined)
        );
    }

    private manageConsequentialCart() {
        return combineLatest([
            this.store.select(selectFlowType),
            this.store.select(v2SelectConsequentialCartType())
        ]).pipe(
            take(1),
            map(([flowType, consequentialCartType]) => ({
                isMultiCart:
                    isConsequentialCartActive(flowType, this.toggleSrv.isConsequentialCartEnabled) &&
                    !!consequentialCartType,
                consequentialCartType
            })),
            tap(({ isMultiCart, consequentialCartType }) => {
                if (isMultiCart) {
                    this.store.dispatch(setV2ConsequentialCartInfo({ consequentialCartId: this.currentCartId }));
                }
            }),
            map(() => true)
        );
    }

    private invalidateCoupon() {
        if (this.toggleSrv.isInvalidateCouponEnabled) {
            return combineLatest([
                this.cartSrv.getCartWithId(this.currentCartId),
                this.store.select(selectSalesProcess),
                this.store.select(selectCartSegment)
            ]).pipe(
                take(1),
                tap(([cart, salesProcess, segment]) =>
                    console.log(cart, salesProcess, segment, convertSegmentD365toApt(segment))
                ),
                mergeMap(([cart, salesProcess, segment]) => {
                    const executionConfig: ExecutionConfig = {
                        checkValidity: false,
                        assign: false,
                        checkAssignment: true,
                    };
                    return this.orderEntryPrv
                        .checkCoupon(
                            this.currentCartId,
                            cart.CouponCodes,
                            convertSegmentD365toApt(segment),
                            salesProcess,
                            executionConfig
                        )
                        .pipe(map(() => true));
                })
            );
        } else {
            return of(true);
        }
    }

    private wrapperAddNewCreditCard(quote: any, req: any): Observable<CartToQuoteResponse> {
        const obs$ =
            req.billingAccount?.paymentTool?.instrumentType === AptPaymentInstrument.CartaCredito &&
            !req.billingAccount?.paymentTool?.id
                ? this.orderEntryPrv.addNewCreditCard(quote.Id, req.channelAndAgency?.salesAgent)
                : of(null);

        return combineLatest([of(quote), obs$]).pipe(map(([quote]) => quote));
    }

    private get currentCartId(): string {
        return CartService.getCurrentCartId();
    }

    private closeD365Case$(quote: any, caseID: string): Observable<CartToQuoteResponse> {
        const upsertCase$ = this.d365Srv.retrieveUpsertCloseCase({ caseID }).pipe(
            take(1),
            mergeMap((retrieveUpsertCase: D365RetrieveUpsertCase) => {
                const upsertCaseReq: UpsertCaseReq = {
                    caseNumber: retrieveUpsertCase[0].ticketnumber,
                    processCode: '001',
                    customer: {
                        customerType: 'ACCOUNT',
                        customerCode: retrieveUpsertCase[0].accountnumber
                    },
                    channel: retrieveUpsertCase[0].caseorigincode,
                    caseCharacteristic: retrieveUpsertCase[0].casecharacteristic,
                    caseLevel1Code: retrieveUpsertCase[0].caselevel1code,
                    caseLevel2Code: retrieveUpsertCase[0].caselevel2code,
                    caseLevel3Code: retrieveUpsertCase[0].caselevel3code,
                    status: '5',
                    caseResolutionReason: 'RISOLTO',
                    caseOrigin: retrieveUpsertCase[0].caseorigin
                };
                return this.d365Srv.upsertCase(upsertCaseReq).pipe(
                    take(1),
                    map((res) => {
                        return res?.APIResult;
                    })
                );
            })
        );
        return combineLatest([of(quote), upsertCase$]).pipe(map(([quote]) => quote));
    }

    private manageGetPhoneCall(): Observable<string> {
        return combineLatest([
            this.store.select(selectAgentInfo),
            this.store.select(selectLead),
            this.store.select(v2SelectIdCase)
        ]).pipe(
            take(1),
            mergeMap(([agentInfo, leadInfo, caseInfo]) => {
                const caseId = caseInfo;
                const profileName = agentInfo?.ProfileName;
                const agent = agentInfo?.SystemUserId;
                const leadId = leadInfo?.leadid;
                const leadIteractionId = leadInfo?.egl_phonecall_interactionid;
                const profileRetrivePhoneCall = this.configSrv.get('profileRetrivePhoneCall', []) as string[];
                if (
                    ((profileRetrivePhoneCall.includes(profileName) && !!leadId && !leadIteractionId) ||
                        (profileRetrivePhoneCall.includes(profileName) && !leadId)) &&
                    !caseId
                ) {
                    return this.d365Srv.getPhoneCall(agent).pipe(
                        take(1),
                        tap((phoneCall) => {
                            this.logger.info(
                                `Retrieve for agent ${agent} - data phoneCall ${JSON.stringify(phoneCall)}`
                            );
                            // Se directionCode === false (inbound) && effectiveEndDate === false (chiamata non terminata) && actualDurationSeconds === false (non so ancora quanto è durata in totale la chiamata)
                            // allora salvo l'id della phoneCall per associarla alla quote che sto per creare
                            if (
                                !phoneCall?.directionCode &&
                                !phoneCall?.effectiveEndDate &&
                                !phoneCall?.actualDurationSeconds
                            ) {
                                this.store.dispatch(setV2IdPhoneCall({ idPhoneCall: phoneCall.interactionId }));
                            }
                        }),
                        map(() => null)
                    );
                }
                return of(null);
            })
        );
    }
}
